import Permission from "../../util/access/Permission";
import KeyIcon from "@mui/icons-material/Key";
import LoggerIcon from '@mui/icons-material/Terminal';
import TeamIcon from '@mui/icons-material/Groups3';
import KeyVaultIcon from "@mui/icons-material/AccountBalance";
import KeyTagIcon from "@mui/icons-material/Label";
import NeighbourhoodIcon from '@mui/icons-material/Cottage';
import RoleIcon from "@mui/icons-material/TheaterComedy";
import SeatingPlanIcon from '@mui/icons-material/Deck';
import EmployeeIcon from "@mui/icons-material/People";
import VisitReportIcon from "@mui/icons-material/Assessment";
import OfficeIcon from '@mui/icons-material/Apartment';
import WorkSiteIcon from '@mui/icons-material/Public';
import NewJoinerIcon from '@mui/icons-material/PersonAdd';
import SlackIcon from "../../components/icons/SlackIcon";
import TokenIcon from '@mui/icons-material/VpnKey';
import {OverridableComponent} from "@mui/material/OverridableComponent";
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    AutocompleteRenderOptionState,
    Box,
    Checkbox,
    Divider,
    IconButton,
    ListItemText,
    Stack,
    SvgIconTypeMap,
    TextField,
    Tooltip
} from "@mui/material";
import NewJoinerChecklistIcon from '@mui/icons-material/Checklist';
import DeskIcon from '@mui/icons-material/TableRestaurant';
import {EntityType} from "../../api/Model";
import ViewIcon from '@mui/icons-material/Visibility';
import AdminIcon from '@mui/icons-material/Settings';
import OperationIcon from '@mui/icons-material/Autorenew';
import RemoveIcon from '@mui/icons-material/Close';
import React, {HTMLAttributes, useCallback, useMemo} from "react";
import {TextFieldProps} from "@mui/material/TextField/TextField";
import {autocompleteChangeEvent, SetState} from "../../util/FormHandlers";


export const entityIcons: Record<EntityType, OverridableComponent<SvgIconTypeMap>> = {
    Employees: EmployeeIcon,
    Offices: OfficeIcon,
    UserRoles: RoleIcon,
    PermanentTokens: TokenIcon,
    Teams: TeamIcon,
    WorkSites: WorkSiteIcon,
    Desks: DeskIcon,
    Floors: OfficeIcon,
    KeyData: KeyIcon,
    SeatingPlans: SeatingPlanIcon,
    NeighbourhoodSnapshots: SeatingPlanIcon,
    KeyTags: KeyTagIcon,
    VisitReports: VisitReportIcon,
    Neighbourhoods: NeighbourhoodIcon,
    KeyVaults: KeyVaultIcon,
    GlobalKeyVaults: KeyVaultIcon,
    SlackSettings: SlackIcon,
    NewJoiners: NewJoinerIcon,
    NewJoinerChecklistItems: NewJoinerChecklistIcon,
};

enum Operation {
    Admin = "Admin",
    Operate = "Operate",
    View = "View",
}

const operationIcons: Record<Operation, OverridableComponent<SvgIconTypeMap>> = {
    Admin: AdminIcon,
    Operate: OperationIcon,
    View: ViewIcon,
};

const operationColor: Record<Operation, string> = {
    Admin: "error.main",
    Operate: "warning.main",
    View: "success.main",
}

const permissionPresentation: Record<Permission, {
    label: string,
    operation: Operation,
} & ({
    entity: EntityType,
    entityIcon?: undefined,
} | {
    entity?: undefined,
    entityIcon: OverridableComponent<SvgIconTypeMap>,
})> = {
    Offices_Read: {
        label: "Offices Read",
        entity: EntityType.Offices,
        operation: Operation.View,
    },
    Offices_Admin: {
        label: "Offices Admin",
        entity: EntityType.Offices,
        operation: Operation.Admin,
    },
    WorkSites_Read: {
        label: "WorkSites Read",
        entity: EntityType.WorkSites,
        operation: Operation.View,
    },
    WorkSites_Admin: {
        label: "WorkSites Admin",
        entity: EntityType.WorkSites,
        operation: Operation.Admin,
    },
    Employees_Read: {
        label: "Employees Read",
        entity: EntityType.Employees,
        operation: Operation.View,
    },
    Employees_Admin: {
        label: "Employees Admin",
        entity: EntityType.Employees,
        operation: Operation.Admin,
    },
    KeyVaults_Read: {
        label: "KeyVaults Read",
        entity: EntityType.KeyVaults,
        operation: Operation.View,
    },
    KeyVaults_Admin: {
        label: "KeyVaults Admin",
        entity: EntityType.KeyVaults,
        operation: Operation.Admin,
    },
    GlobalKeyVaults_Read: {
        label: "GlobalKeyVaults Read",
        entity: EntityType.GlobalKeyVaults,
        operation: Operation.View,
    },
    KeyTags_Read: {
        label: "KeyTags Read",
        entity: EntityType.KeyTags,
        operation: Operation.View,
    },
    KeyTags_Admin: {
        label: "KeyTags Admin",
        entity: EntityType.KeyTags,
        operation: Operation.Admin,
    },
    KeyData_Read: {
        label: "KeyData Read",
        entity: EntityType.KeyData,
        operation: Operation.View,
    },
    KeyData_Operate: {
        label: "KeyData Operate",
        entity: EntityType.KeyData,
        operation: Operation.Operate,
    },
    KeyData_Admin: {
        label: "KeyData Admin",
        entity: EntityType.KeyData,
        operation: Operation.Admin,
    },
    Teams_Read: {
        label: "Teams Read",
        entity: EntityType.Teams,
        operation: Operation.View,
    },
    VisitReports_Read: {
        label: "VisitReports Read",
        entity: EntityType.VisitReports,
        operation: Operation.View,
    },
    VisitReports_Admin: {
        label: "VisitReports Admin",
        entity: EntityType.VisitReports,
        operation: Operation.Admin,
    },
    Neighbourhoods_Read: {
        label: "Neighbourhoods Read",
        entity: EntityType.Neighbourhoods,
        operation: Operation.View,
    },
    Neighbourhoods_Admin: {
        label: "Neighbourhoods Admin",
        entity: EntityType.Neighbourhoods,
        operation: Operation.Admin,
    },
    Desks_Read: {
        label: "Desks Read",
        entity: EntityType.Desks,
        operation: Operation.View,
    },
    Floors_Read: {
        label: "Floors Read",
        entity: EntityType.Floors,
        operation: Operation.View,
    },
    SeatingPlans_Read: {
        label: "SeatingPlans Read",
        entity: EntityType.SeatingPlans,
        operation: Operation.View,
    },
    SeatingPlans_Execute: {
        label: "SeatingPlans Execute",
        entity: EntityType.SeatingPlans,
        operation: Operation.Operate,
    },
    SeatingPlans_Admin: {
        label: "SeatingPlans Admin",
        entity: EntityType.SeatingPlans,
        operation: Operation.Admin,
    },
    NewJoiners_Read: {
        label: "NewJoiners Read",
        entity: EntityType.NewJoiners,
        operation: Operation.View,
    },
    NewJoiners_Admin: {
        label: "NewJoiners Admin",
        entity: EntityType.NewJoiners,
        operation: Operation.Admin,
    },
    NewJoinerChecklistItems_Read: {
        label: "NewJoinerChecklistItems Read",
        entity: EntityType.NewJoinerChecklistItems,
        operation: Operation.View,
    },
    NewJoinerChecklistItems_Admin: {
        label: "NewJoinerChecklistItems Admin",
        entity: EntityType.NewJoinerChecklistItems,
        operation: Operation.Admin,
    },
    SlackSettings_Read: {
        label: "SlackSettings Read",
        entity: EntityType.SlackSettings,
        operation: Operation.View,
    },
    SlackSettings_Admin: {
        label: "SlackSettings Admin",
        entity: EntityType.SlackSettings,
        operation: Operation.Admin,
    },
    UserRoles_Read: {
        label: "UserRoles Read",
        entity: EntityType.UserRoles,
        operation: Operation.View,
    },
    UserRoles_Admin: {
        label: "UserRoles Admin",
        entity: EntityType.UserRoles,
        operation: Operation.Admin,
    },
    PermanentTokens_Read: {
        label: "PermanentTokens Read",
        entity: EntityType.PermanentTokens,
        operation: Operation.View,
    },
    PermanentTokens_Admin: {
        label: "PermanentTokens Admin",
        entity: EntityType.PermanentTokens,
        operation: Operation.Admin,
    },
    ClientLoggers_Admin: {
        label: "ClientLoggers Admin",
        entityIcon: LoggerIcon,
        operation: Operation.Admin,
    },
};


const tokenPermissions = Object
    .values(Permission)
    .filter(permission => permission !== Permission.ClientLoggers_Admin);

function PermissionChip({permission, onRemovePermission}: {
    permission: Permission,
    onRemovePermission?: (permission: Permission) => void,
}) {
    const presentation = permissionPresentation[permission];
    const color = operationColor[presentation.operation];
    const EntityIcon = presentation.entityIcon ?? entityIcons[presentation.entity];
    const OperationIcon = operationIcons[presentation.operation];

    return (
        <Tooltip title={presentation.label}>
            <Stack
                component="span"
                direction="row"
                gap={1}
                alignItems="center"
                sx={{
                    backgroundColor: color,
                    borderRadius: "8px",
                    py: 0.5,
                    px: 1,
                    width: 'fit-content'
                }}
            >
                <EntityIcon sx={{fontSize: '20px', color: 'error.contrastText'}}/>
                <Divider orientation="vertical" flexItem sx={{borderColor: 'error.contrastText'}}/>
                <Stack
                    component="span"
                    direction="row"
                    alignItems="center"
                >
                    <OperationIcon sx={{fontSize: '20px', color: 'error.contrastText'}}/>
                    {onRemovePermission && (
                        <IconButton
                            aria-label={`Remove ${presentation.label}`}
                            size="small"
                            onMouseDown={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                            }}
                            onClick={(event) => {
                                event.stopPropagation();
                                onRemovePermission(permission);
                            }}
                            sx={{
                                color: 'error.contrastText',
                                p: 0,
                                ml: 0.25,
                            }}
                        >
                            <RemoveIcon sx={{fontSize: '14px'}}/>
                        </IconButton>
                    )}
                </Stack>
            </Stack>
        </Tooltip>

    );
}

export function PermissionList({permissions, onRemovePermission}: {
    permissions: Permission[],
    onRemovePermission?: (permission: Permission) => void,
}) {
    return (
        <>{permissions.map(permission => (
            <PermissionChip
                key={permissionPresentation[permission].label}
                permission={permission}
                onRemovePermission={onRemovePermission}
            />
        ))}</>
    );
}

export function PermissionSelect({value, setValue, ...props}: {
    value: Permission[],
    setValue: SetState<Permission[]>,
} & TextFieldProps) {
    const handlePermissionChange = useMemo(
        () => autocompleteChangeEvent<Permission[]>(newValue => setValue(newValue)),
        [setValue],
    );

    const handleRemovePermission = useCallback((permission: Permission) => {
        setValue(prevValue => prevValue.filter(selectedPermission => selectedPermission !== permission));
    }, [setValue]);

    const renderOption = useCallback(({key, ...props}: HTMLAttributes<HTMLLIElement> & {
        key: any
    }, option: Permission, {selected}: AutocompleteRenderOptionState) => {
        const presentation = permissionPresentation[option];
        const EntityIcon = presentation.entityIcon ?? entityIcons[presentation.entity];
        const OperationIcon = operationIcons[presentation.operation];
        return (
            <li key={key} {...props}>
                <Checkbox checked={selected} sx={{mr: 1}}/>
                <Stack direction="row" gap={1} alignItems="center" sx={{minWidth: 0}}>
                    <EntityIcon sx={{fontSize: '20px'}}/>
                    <OperationIcon sx={{fontSize: '16px'}}/>
                    <ListItemText primary={presentation.label}/>
                </Stack>
            </li>
        );
    }, []);

    const renderInput = useCallback((params: AutocompleteRenderInputParams) => (
        <TextField sx={{gap: '8px'}} {...params} {...props}/>
    ), [props]);

    return (
        <Autocomplete<Permission, true>
            multiple
            fullWidth
            disableCloseOnSelect
            options={tokenPermissions}
            value={value}
            onChange={handlePermissionChange}
            getOptionLabel={option => permissionPresentation[option].label}
            renderOption={renderOption}
            renderValue={(selected) => (
                <>{
                    selected.map(permission => (
                        <Box key={permissionPresentation[permission].label} sx={{mr: 0.5, mt: '4px'}}>
                            <PermissionChip
                                permission={permission}
                                onRemovePermission={handleRemovePermission}
                            />
                        </Box>
                    ))
                }</>
            )}
            renderInput={renderInput}
        />
    );
}
